import Phaser from 'phaser';
import { Key9Scene } from './game/Key9Scene';
import type { Key9Puzzle } from './types/puzzle';
import { resolveDailyPuzzle, type PuzzleManifestEntry } from './logic/dailyPuzzle';
import { getLocalDateString, getNextLocalMidnight, getNextPuzzleCountdownModel } from './logic/dailyCountdown';
import { createDailyPuzzleIdentity, type DailyPuzzleIdentity } from './logic/puzzleIdentity';
import { classicShareText, classicStorageKey, formatSolveTime, getClassicPlayCopy, hasClassicWork, shouldOfferReturnShortcut, type ClassicProgress } from './logic/classicDaily';
import { createClassicSaveSession, loadClassicProgress } from './storage/classicSave';
import { createSolveClock } from './logic/solveClock';
import { validatePuzzleShape } from './logic/key9Validator';
import { CLASSIC_BOARD_GEOMETRY, getAccessibleCells, getBoardEditAction } from './logic/classicBoardAccess';
import { createAccessibleBoard } from './ui/classicBoardAccess';
import { getNextSolveIdea } from './logic/nextSolveIdea';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
let game: Phaser.Game | undefined;
let scene: Key9Scene | undefined;
let puzzle: Key9Puzzle;
let identity: DailyPuzzleIdentity;
let progress: ClassicProgress;
let bootToken = 0;
let clock = createSolveClock();
let saveSession: ReturnType<typeof createClassicSaveSession>;
let saveConflict = false;
let pageActive = true;
let midnight = 0;
let tick: ReturnType<typeof setInterval> | undefined;
let ready = false;
let resumed = false;
let progressSaved = false;
let selectedCellIndex: number | null = null;
const standaloneDisplay = window.matchMedia('(display-mode: standalone)');
const accessibleBoard = createAccessibleBoard($('ds-board-grid'), (index) => {
  if (canPlay()) scene?.selectFullCell(index);
});

function renderReturnShortcut() {
  const standalone = standaloneDisplay.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  $('ds-return-shortcut').hidden = !shouldOfferReturnShortcut(ready ? progress.phase : 'loading', standalone);
}
standaloneDisplay.addEventListener('change', renderReturnShortcut);

function renderPlayCopy() {
  if (!ready) return;
  const copy = getClassicPlayCopy(puzzle, progress, { resumed, saved: progressSaved, conflict: saveConflict });
  $('ds-status-label').textContent = copy.title;
  $('ds-save-status').textContent = copy.footer;
}

function renderAccessibleBoard() {
  if (!progress) return;
  accessibleBoard.render(puzzle, progress.board, progress.notes, saveConflict || progress.phase === 'full_done', selectedCellIndex);
}

async function readJson(path: string): Promise<any> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(path, { cache: 'no-cache', signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } finally { clearTimeout(timeout); }
}

async function loadPuzzle(dailyDate: string) {
  let entry: PuzzleManifestEntry | undefined;
  try {
    entry = resolveDailyPuzzle(await readJson('/puzzles/manifest.json'), dailyDate).entry;
  } catch { console.warn('Daily manifest unavailable; using the fallback puzzle.'); }
  const paths = [...new Set([entry?.path, `/puzzles/${dailyDate.slice(0, 4)}/${dailyDate}.json`, '/puzzles/2026/2026-06-12.json'].filter(Boolean))] as string[];
  for (const path of paths) {
    try {
      const candidate: Key9Puzzle = await readJson(path);
      if (validatePuzzleShape(candidate).length) throw new Error('Invalid puzzle');
      return { puzzle: candidate, identity: createDailyPuzzleIdentity({ dailyDate, puzzle: candidate, puzzlePath: path, manifestEntry: entry?.path === path ? entry : null }) };
    } catch { console.warn(`Puzzle unavailable: ${path}`); }
  }
  throw new Error('Puzzle unavailable');
}

function seconds() {
  return clock.seconds();
}

function suspendForSaveConflict() {
  if (saveConflict) return;
  saveConflict = true;
  progressSaved = false;
  clock.pause();
  if (scene?.input) scene.input.enabled = false;
  $<HTMLFieldSetElement>('ds-classic-controls').disabled = true;
  $('ds-storage-conflict').hidden = false;
  $('ds-save-warning').hidden = true;
  for (const id of ['ds-share', 'ds-copy']) $<HTMLButtonElement>(id).disabled = true;
  renderAccessibleBoard();
  renderPlayCopy();
}

function canPlay() {
  if (!ready || !pageActive || document.hidden || progress.phase === 'full_done' || saveConflict) return false;
  if (saveSession.hasConflict()) { suspendForSaveConflict(); return false; }
  return true;
}

function persist() {
  if (!ready || saveConflict) return;
  progress.seconds = seconds();
  const result = saveSession.save(progress);
  progressSaved = result.status === 'saved';
  if (result.status === 'conflict') { suspendForSaveConflict(); return; }
  if (result.status === 'saved') {
    clock.ensureSeconds(result.seconds);
    progress.seconds = seconds();
  }
  $('ds-save-warning').hidden = result.status === 'saved';
  renderPlayCopy();
}

function updateClock() {
  if (!ready) return;
  $('ds-timer').textContent = formatSolveTime(seconds());
  if (progress.phase !== 'full_done') return;
  const model = getNextPuzzleCountdownModel(new Date(), { fullDone: true, timeUntilMs: midnight - Date.now() });
  $('ds-next-puzzle').textContent = model.label;
  $('ds-load-new-puzzle').hidden = !model.ready;
}

function renderPhase() {
  const done = progress.phase === 'full_done';
  const shell = $('ds-classic');
  shell.dataset.phase = progress.phase;
  shell.dataset.terminalVisible = String(done);
  $('ds-classic-controls').hidden = done;
  $('ds-classic-result').hidden = !done;
  const idea = getNextSolveIdea(progress.phase, identity.dailyDate);
  const ideaPanel = $<HTMLDetailsElement>('ds-next-solve-idea');
  ideaPanel.hidden = !idea;
  if (idea) {
    $('ds-next-solve-title').textContent = idea.title;
    $('ds-next-solve-text').textContent = idea.text;
    $<HTMLAnchorElement>('ds-next-solve-link').href = idea.href;
  } else {
    ideaPanel.open = false;
  }
  renderPlayCopy();
  $('ds-progress').textContent = `${progress.board.filter((v) => v !== '0').length}/81`;
  if (done) {
    $('ds-result-time').textContent = formatSolveTime(progress.seconds);
    $('ds-result-hints').textContent = String(progress.hints);
    $('ds-result-date').textContent = identity.dailyDate;
    $('ds-result-note').textContent = progress.hints ? 'A little help. A complete solve.' : 'All yours. No hints used.';
  }
  updateClock();
  renderAccessibleBoard();
  renderReturnShortcut();
}

async function boot() {
  const token = ++bootToken;
  clock.pause();
  ready = false;
  resumed = false;
  progressSaved = false;
  $('ds-status-label').textContent = 'Today’s Sudoku';
  $('ds-save-status').textContent = 'At your pace.';
  renderReturnShortcut();
  clearInterval(tick);
  $('ds-loading').hidden = false;
  $('ds-loading-copy').textContent = 'Getting today’s puzzle…';
  $('ds-retry-load').hidden = true;
  $('ds-play-surface').hidden = true;
  try {
    const date = new Date();
    const loaded = await loadPuzzle(getLocalDateString(date));
    if (token !== bootToken) return;
    puzzle = loaded.puzzle;
    identity = loaded.identity;
    let storage: Pick<Storage, 'getItem' | 'setItem'>;
    try { storage = localStorage; }
    catch { storage = { getItem: () => null, setItem: () => { throw new Error('Storage unavailable'); } }; }
    // Capture the expected record before hydration; changed records cannot be overwritten.
    saveSession = createClassicSaveSession(storage, puzzle, identity);
    progress = loadClassicProgress(storage, puzzle, identity);
    resumed = progress.phase === 'full_in_progress' && hasClassicWork(puzzle, progress);
    selectedCellIndex = null;
    clock = createSolveClock(progress.seconds);
    saveConflict = false;
    $('ds-storage-conflict').hidden = true;
    $<HTMLFieldSetElement>('ds-classic-controls').disabled = false;
    for (const id of ['ds-share', 'ds-copy']) $<HTMLButtonElement>(id).disabled = false;
    midnight = getNextLocalMidnight(date).getTime();
    game?.destroy(true);
    $('daysudoku-game').replaceChildren();
    $('ds-play-surface').hidden = false;
    game = new Phaser.Game({
      type: Phaser.AUTO, parent: 'daysudoku-game', width: CLASSIC_BOARD_GEOMETRY.width, height: CLASSIC_BOARD_GEOMETRY.height,
      backgroundColor: '#f5f7fb', render: { antialias: true },
      scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH }, scene: []
    });
    scene = new Key9Scene();
    // Hydrated values and notes are supplied before Phaser's first visible frame.
    game.scene.add('Key9Scene', scene, true, { puzzle, classic: true, mode: 'full', progress: {
      phase: progress.phase, fullBoard: progress.board, fullNotes: progress.notes, fullHints: progress.hints
    } });
    ready = true;
    if (progress.phase === 'full_in_progress' && pageActive && !document.hidden) clock.resume();
    $('ds-loading').hidden = true;
    $('ds-date').textContent = new Date(`${identity.dailyDate}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    renderPhase();
    persist();
    let ticks = 0;
    tick = setInterval(() => { updateClock(); if (++ticks % 15 === 0 && progress.phase !== 'full_done' && !document.hidden) persist(); }, 1000);
  } catch {
    if (token !== bootToken) return;
    $('ds-loading-copy').textContent = 'We couldn’t load today’s puzzle. Please try again.';
    $('ds-retry-load').hidden = false;
    $('ds-play-surface').hidden = true;
  }
}

window.addEventListener('daysudoku:full-progress', (event) => {
  if (!canPlay()) return;
  const detail = (event as CustomEvent).detail;
  const changedCell = progress.board.findIndex((value, index) => value !== detail.board[index] || progress.notes[index] !== detail.notes[index]);
  if (changedCell >= 0) resumed = false;
  progress = { ...progress, board: [...detail.board], notes: [...detail.notes], hints: detail.fullHints };
  persist();
  renderPhase();
  if (changedCell >= 0) $('ds-board-announcement').textContent = getAccessibleCells(puzzle, progress.board, progress.notes, false)[changedCell].label;
});
window.addEventListener('daysudoku:full-complete', (event) => {
  if (!canPlay()) return;
  const detail = (event as CustomEvent).detail;
  clock.pause();
  selectedCellIndex = null;
  progress = { ...progress, phase: 'full_done', board: [...detail.board], notes: [...detail.notes], hints: detail.fullHints, seconds: seconds() };
  persist();
  renderPhase();
  $('ds-board-announcement').textContent = 'Sudoku complete. Your result is below the board.';
});
window.addEventListener('daysudoku:key9-status', (event) => {
  const detail = (event as CustomEvent).detail;
  selectedCellIndex = detail.selectedCellIndex ?? null;
  $<HTMLButtonElement>('ds-undo').disabled = !detail.canUndo;
  $<HTMLButtonElement>('ds-erase').disabled = !detail.canErase;
  $('ds-notes').setAttribute('aria-pressed', String(!!detail.notesMode));
  $('ds-hint').setAttribute('aria-disabled', String(!detail.canHint));
  $('ds-hints-used').textContent = detail.fullHints ? `Hints ${detail.fullHints}` : '';
  renderAccessibleBoard();
});
window.addEventListener('daysudoku:instruction', (event) => {
  const message = (event as CustomEvent<{ message: string }>).detail.message;
  if (message.startsWith('Select ') || message.startsWith('Notes ') || message === 'That entry is already set.') {
    $('ds-board-announcement').textContent = message;
  }
});

for (const action of ['undo', 'erase', 'hint', 'notes'] as const) {
  $(`ds-${action}`).addEventListener('click', () => {
    if (!canPlay()) return;
    if (action === 'notes') scene?.toggleNotes();
    else scene?.[action]();
  });
}
document.querySelectorAll<HTMLButtonElement>('[data-digit]').forEach((button) => {
  button.addEventListener('click', () => { if (canPlay()) scene?.enterDigit(Number(button.dataset.digit)); });
});
$('ds-classic').addEventListener('keydown', (event) => {
  const target = event.target as HTMLElement;
  if (!target.closest('[role="gridcell"], #ds-classic-controls button')) return;
  const action = getBoardEditAction(event);
  if (action == null || !canPlay()) return;
  if (typeof action === 'number') scene?.enterDigit(action);
  else if (action === 'notes') scene?.toggleNotes();
  else scene?.[action]();
  event.preventDefault();
});
function syncVisibility() {
  if (!ready) return;
  if (!pageActive || document.hidden) { clock.pause(); persist(); }
  else if (canPlay()) clock.resume();
}
document.addEventListener('visibilitychange', syncVisibility);
window.addEventListener('pagehide', () => { pageActive = false; syncVisibility(); });
window.addEventListener('pageshow', () => { pageActive = true; syncVisibility(); renderReturnShortcut(); });
window.addEventListener('storage', (event) => {
  if (ready && (event.key === classicStorageKey(identity) || event.key === null) && saveSession.hasConflict()) suspendForSaveConflict();
});
$('ds-load-saved-progress').addEventListener('click', () => location.reload());
$('ds-retry-load').addEventListener('click', () => void boot());
$('ds-load-new-puzzle').addEventListener('click', () => location.reload());

async function share(copyOnly = false) {
  if (!ready || saveConflict || progress.phase !== 'full_done') return;
  const text = classicShareText(progress);
  try {
    if (!copyOnly && navigator.share) await navigator.share({ title: 'DaySudoku', text });
    else { await navigator.clipboard.writeText(text); $('ds-share-feedback').textContent = 'Result copied.'; }
  } catch (error) {
    if ((error as Error).name === 'AbortError') return;
    $('ds-share-fallback').hidden = false;
    const field = $<HTMLTextAreaElement>('ds-share-text');
    field.value = text;
    field.focus(); field.select();
    $('ds-share-feedback').textContent = 'Select and copy your result below.';
  }
}
$('ds-share').addEventListener('click', () => void share());
$('ds-copy').addEventListener('click', () => void share(true));
void boot();
